import { createStore, useStore } from 'zustand';
import {
  BLOCKED_ACCOUNT_COOKIE_NAME,
  deleteCookie,
  getCookie,
  parseBlockedAccountCookieValue,
  serializeBlockedAccountCookieValue,
  type BlockedAccountCookieDto,
} from '@skillarea/lib-shared';

import { onboardStore } from '~/stores/onboardStore';

type TBlockedAccountStoreOptions = {
  clearClientSession?: boolean;
};

type TBlockedAccountState = {
  banStatus: BlockedAccountCookieDto | null;
  isOpen: boolean;
  dismiss: () => void;
  handleRestrictionEvent: (banStatus: BlockedAccountCookieDto) => void;
  show: (
    banStatus: BlockedAccountCookieDto,
    options?: TBlockedAccountStoreOptions,
  ) => void;
  syncFromCookie: (
    options?: TBlockedAccountStoreOptions,
  ) => BlockedAccountCookieDto | undefined;
};

function persistBlockedAccountCookie(banStatus: BlockedAccountCookieDto): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${BLOCKED_ACCOUNT_COOKIE_NAME}=${serializeBlockedAccountCookieValue(banStatus)}; path=/; SameSite=Lax`;
}

function readBlockedAccountCookie(): BlockedAccountCookieDto | undefined {
  return parseBlockedAccountCookieValue(getCookie(BLOCKED_ACCOUNT_COOKIE_NAME));
}

function clearBlockedAccountCookie(): void {
  deleteCookie(BLOCKED_ACCOUNT_COOKIE_NAME);
}

export const accountBlockedStore = createStore<TBlockedAccountState>((set) => ({
  banStatus: null,
  isOpen: false,

  dismiss() {
    clearBlockedAccountCookie();
    set({ banStatus: null, isOpen: false });
  },

  handleRestrictionEvent(banStatus) {
    persistBlockedAccountCookie(banStatus);
    set({ banStatus, isOpen: true });

    void onboardStore
      .getState()
      .signOut()
      .catch((error: unknown) => {
        console.error('Failed to sign out after restriction event', error);
      })
      .finally(() => {
        onboardStore.getState().clearClientSession();
        persistBlockedAccountCookie(banStatus);
        set({ banStatus, isOpen: true });
      });
  },

  show(banStatus, options) {
    persistBlockedAccountCookie(banStatus);

    if (options?.clearClientSession) {
      onboardStore.getState().clearClientSession();
    }

    set({ banStatus, isOpen: true });
  },

  syncFromCookie(options) {
    const banStatus = readBlockedAccountCookie();
    if (!banStatus) {
      set({ banStatus: null, isOpen: false });
      return;
    }

    if (options?.clearClientSession) {
      onboardStore.getState().clearClientSession();
    }

    set({ banStatus, isOpen: true });
    return banStatus;
  },
}));

export const useAccountBlockedStore = <T>(
  selector: (state: TBlockedAccountState) => T,
) => useStore(accountBlockedStore, selector);
